import { useState } from "react";
import { Col, Row } from "react-grid-system";
import { AccountSearchResult } from "../../lib/api/models";
import {
  useAccountSearch,
  useAccountSearchByThreeOfFour,
} from "../../lib/api/queries";
import {
  AccountFound,
  AccountNotFound,
  AccountSearchResultsTable,
} from "../../modules/AccountSearch";

import Container from "../../modules/common/Container";
import { NetworkModal } from "../../modules/common/modals";
import styles from "./AccountSearchPage.module.css";
import SingularSearchForm from "./SingularSearchForm";
import ThreeOfFourSearchForm from "./ThreeOfFourSearchForm";
import ChannelForm from "./ChannelForm";
import DialerPull from "./DialerPull";
import WarningBanner from "../../modules/WarningBanner";
import { NetworkDisplayError } from "../../lib/api/endpoints";
import { useDialerPullState } from "../../lib/dialerPullContext";

const strings = {
  title: "Search",
  listenerFailure:
    "We are experiencing difficulty automatically pulling call information from the dialer at this time. You might be required to manually search for your next call. Please search accounts and complete calls as appropriate.",
};

const AccountSearchPage = () => {
  const { callContext } = useDialerPullState();

  const [displayError, setDisplayError] = useState<NetworkDisplayError>();
  const [accountToView, setAccountToView] = useState<AccountSearchResult>();
  const [displayListenerWarning, setDisplayListenerWarning] = useState(false);
  const [overrideDisplayListenerWarning, setOverrideDisplayListenerWarning] =
    useState(false);

  const {
    mutateAsync: accountSearch,
    data: accountSearchResponse,
    isLoading: isAccountSearchLoading,
    reset: resetAccountSearch,
  } = useAccountSearch();

  const {
    mutateAsync: accountSearchByThreeOfFour,
    data: accountSearchByThreeOfFourResponse,
    isLoading: isAccountSearchByThreeOfFourLoading,
    reset: resetAccountSearchByThreeOfFour,
  } = useAccountSearchByThreeOfFour();

  const resetAccountSearchQueries = () => {
    setAccountToView(undefined);
    resetAccountSearch();
    resetAccountSearchByThreeOfFour();
  };

  const accounts =
    accountSearchResponse?.accounts ??
    accountSearchByThreeOfFourResponse?.accounts;

  return (
    <main>
      {displayListenerWarning && !overrideDisplayListenerWarning ? (
        <WarningBanner
          message={strings.listenerFailure}
          onDismiss={() => setOverrideDisplayListenerWarning(true)}
        />
      ) : null}
      <Container>
        <Row>
          <Col offset={{ xs: 1 }}>
            <h1 className={styles.title}>{strings.title}</h1>
          </Col>
        </Row>
        <Row>
          <Col xs={10} offset={{ xs: 1 }}>
            <DialerPull setDisplayListenerWarning={setDisplayListenerWarning} />
          </Col>
        </Row>
        <Row>
          <Col xs={2} offset={{ xs: 1 }}>
            <ChannelForm />
          </Col>
          <Col xs={8} className={styles.searchCol}>
            <SingularSearchForm
              isDisabled={isAccountSearchByThreeOfFourLoading}
              isLoading={isAccountSearchLoading}
              resetAccountSearchQueries={resetAccountSearchQueries}
              accountSearch={accountSearch}
              accountSearchResponse={accountSearchResponse}
              setDisplayError={setDisplayError}
              setAccountToView={setAccountToView}
            />
            <ThreeOfFourSearchForm
              isDisabled={isAccountSearchLoading}
              isLoading={isAccountSearchByThreeOfFourLoading}
              resetAccountSearchQueries={resetAccountSearchQueries}
              accountSearchByThreeOfFour={accountSearchByThreeOfFour}
              accountSearchByThreeOfFourResponse={
                accountSearchByThreeOfFourResponse
              }
              setDisplayError={setDisplayError}
              setAccountToView={setAccountToView}
            />
          </Col>
        </Row>
        {accounts?.length === 0 ? (
          <Row>
            <Col xs={8} offset={{ xs: 3 }}>
              <AccountNotFound
                className={styles.card}
                titleClassName={styles.heading}
              />
            </Col>
          </Row>
        ) : accounts && accounts?.length > 0 && accountToView ? (
          <Row>
            <Col xs={8} offset={{ xs: 3 }}>
              <AccountFound
                account={accountToView}
                className={styles.card}
                titleClassName={styles.heading}
                dwbuid={accountToView.dwbuid}
                callContext={callContext}
              />
            </Col>
          </Row>
        ) : accounts && accounts?.length > 0 ? (
          <Row>
            <Col xs={10} offset={{ xs: 1 }}>
              <AccountSearchResultsTable
                accounts={accounts}
                className={styles.accountSearchResultsTable}
                onClickAccount={setAccountToView}
              />
            </Col>
          </Row>
        ) : null}
      </Container>

      <NetworkModal
        status={displayError?.status}
        message={displayError?.message}
        onRequestClose={() => setDisplayError(undefined)}
      />
    </main>
  );
};

export default AccountSearchPage;
